import { ChangeEvent, useCallback, useEffect, useRef, useState } from "react";
import { Text, Date, Button, Number, Timezone } from "../../../controls";
import Form from "react-bootstrap/Form";
import { Link, useNavigate } from "react-router-dom";
import { Utils, Constants } from "../../../utils";
import CategoryDropdown from "../../../components/CategoryDropdown";
import SubCategoryDropdown from "../../../components/SubCategoryDropdown";
import MediumDropdown from "../../../components/MediumDropdown";
import { usePersistStore, useStore } from "../../../store";
import { BlobService, TrustapUserService } from "../../../services";
import { useOtherCategory } from "../../../hooks/queries";
import { PutBlobResult, put } from "@vercel/blob";
import { v4 as uuid } from "uuid";
import CommonModal from "../../../components/CommonModal";
import GetVerifiedFirst from "../../../components/GetVerifiedFirst";
import ImageLightbox from "../../../components/ImageLightbox";
import moment from "../../../utils/moment";

function Create() {
  const navigate = useNavigate();
  const {
    token,
    user,
    adInProgress,
    setAdInProgress,
    currentLocation,
    isVerifiedUser,
  } = usePersistStore((s: any) => s);
  const { setInfoMessage, setErrorMessage, setSuccessMessage, setIsLoading } =
    useStore((s: any) => s);
  const [dragging, setDragging] = useState(false);
  const inputFile = useRef<HTMLInputElement | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<any[]>([]);
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);
  const [showGetVerifiedFirst, setShowGetVerifiedFirst] = useState(false);
  const [gateReason, setGateReason] = useState<
    "not-verified" | "incomplete-profile" | "no-full-trustap"
  >("not-verified");
  const [showLinkTrustap, setShowLinkTrustap] = useState(false);
  const [values, setValues] = useState({
    title: "",
    categoryId: "",
    otherCategoryName: "",
    subCategoryId: "",
    otherSubCategoryName: "",
    mediumId: "",
    otherMediumName: "",
    height: "",
    width: "",
    depth: "",
    price: "",
    biddingStarts: "",
    biddingEnds: "",
    timezone: user?.timezone || "",
    description: "",
    weight: "",
  });
  const [validationMessage, setValidationMessage] = useState<string>("");

  // Cached "Other" category bundle — shared across the listing forms, so
  // revisiting Create/Preview/Update doesn't refetch it.
  const { data: others = null, error: othersError } = useOtherCategory();
  useEffect(() => {
    if (othersError) setErrorMessage((othersError as Error).message);
  }, [othersError, setErrorMessage]);

  // Route-level gate: if the seller is unverified or has an incomplete
  // profile, block the form here too — the entry-point click guards
  // (Sidebar, List, Seller home) cover normal navigation, but a direct
  // URL hit must not bypass them.
  useEffect(() => {
    const gate = Utils.canCreateListing(user, isVerifiedUser);
    if (!gate.ok) {
      setGateReason(gate.reason);
      setShowGetVerifiedFirst(true);
    }
  }, [user, isVerifiedUser]);

  // Seller must have a full Trustap account before they can list — otherwise
  // they will not be able to claim their payout when the item sells.
  useEffect(() => {
    if (!token) return;
    TrustapUserService.getMyTrustapAccount(token).then((result: any) => {
      const hasFullAccount = result?.data === true;
      if (!hasFullAccount) setShowLinkTrustap(true);
    });
  }, [token]);

  const handleDragIn = useCallback((e: any) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(true);
  }, []);

  const handleDragOut = useCallback((e: any) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: any) => {
      e.preventDefault();
      e.stopPropagation();
      setDragging(false);
      const files = e.dataTransfer?.files;
      if (!files?.length) return;
      // Reuse the same path as the file input — synthesize the event shape.
      handleFileChange({ target: { files } } as any);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const handleFileChange = useCallback(
    (e: any) => {
      const fileList = e?.target?.files;
      if (!fileList?.length) return;
      // Snapshot the FileList into a plain array BEFORE resetting the
      // input below — clearing input.value also drains the live FileList,
      // so iterating it inside the (deferred) functional updater would
      // otherwise see zero files.
      const picked = Array.from(fileList) as File[];
      // Reset the input so picking the same file again (e.g. after the
      // user removed it) still fires a change event.
      if (e?.target) e.target.value = "";

      setSelectedFiles((prev: any[]) => {
        // Append on top of the current selection so picking from a new
        // folder accumulates (rather than replacing the previous batch).
        // Dedupe by name+size so the same file picked twice in a row
        // doesn't double up.
        const next = [...prev];
        const seen = new Set(next.map((f) => `${f?.name}__${f?.size}`));
        let truncated = false;
        for (const file of picked) {
          if (next.length >= Constants.MAX_FILES) {
            truncated = true;
            break;
          }
          const key = `${file?.name}__${file?.size}`;
          if (seen.has(key)) continue;
          seen.add(key);
          next.push({
            img: URL.createObjectURL(file),
            name: file?.name,
            size: file?.size,
            file,
          });
        }
        if (truncated) {
          setInfoMessage(
            `You can choose upto ${Constants.MAX_FILES} images.`
          );
        }
        return next;
      });
    },
    [setInfoMessage]
  );

  const handleRemoveImage = useCallback(
    (e: any, name: string) => {
      e?.preventDefault();
      e?.stopPropagation();
      let t: any[] = selectedFiles?.filter(
        (file: any) => file?.name?.toLowerCase() !== name?.toLowerCase()
      );
      setSelectedFiles(t);
    },
    [selectedFiles]
  );

  const handleInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
      setValues({
        ...values,
        [name]: value,
      });
    },
    [values]
  );

  const handleSubmit = useCallback(
    async (e: any) => {
      e.preventDefault();
      setValidationMessage("");
      if (selectedFiles?.length === 0)
        return setValidationMessage("Please provide at least one image.");
      if (!values?.title)
        return setValidationMessage("Please provide a title.");
      if (!values?.categoryId)
        return setValidationMessage("Please choose a category.");
      if (!values?.subCategoryId)
        return setValidationMessage("Please choose a sub category.");
      if (!values?.mediumId)
        return setValidationMessage(
          "Please choose the medium of this art work."
        );
      if (!values?.height)
        return setValidationMessage("Please provide the height.");
      if (!values?.width)
        return setValidationMessage("Please provide the width.");
      if (!values?.depth)
        return setValidationMessage("Please provide the depth.");
      if (!values?.weight)
        return setValidationMessage("Please provide the weight.");
      if (!values?.price)
        return setValidationMessage("Please provide the price.");
      if (parseFloat(values?.price) < Constants.ARTWORK_MIN_PRICE)
        return setValidationMessage(
          `Minimum price should be ${Utils.formatToCurrency(
            Constants.ARTWORK_MIN_PRICE.toString()
          )}.`
        );
      if (!values?.biddingStarts)
        return setValidationMessage("Please provide bid opening date.");
      if (!values?.biddingEnds)
        return setValidationMessage("Please provide bid closing date.");
      if (
        moment(values?.biddingStarts).isBefore(
          moment().add(Constants.BIDDING_STARTS_MIN_DAYS_AHEAD, "days").startOf("day")
        )
      )
        return setValidationMessage(
          `Bid opens date must be at least ${Constants.BIDDING_STARTS_MIN_DAYS_AHEAD} days from today.`
        );
      if (
        moment(values?.biddingStarts).isSameOrAfter(moment(values?.biddingEnds))
      )
        return setValidationMessage(
          "Bid ends date should be after than bid opens date."
        );
      if (
        moment(values?.biddingEnds).isSameOrAfter(
          moment(values?.biddingStarts).add(3, "months")
        )
      )
        return setValidationMessage(
          "Bid ends date must be within 3 months of bid opens date."
        );
      if (!values?.timezone)
        return setValidationMessage("Please select a timezone.");
      if (!values?.description)
        return setValidationMessage("Please provide a description.");

      let files = selectedFiles?.map((k: any) => k?.file);
      setIsLoading(true);

      let images: string[] = [];
      for (let i = 0; i < files?.length; i++) {
        let file = files[i];
        const ext = Utils.getFileExtension(file?.name);
        const filename = `${uuid()}.${ext}`;
        const tokenResult = await BlobService.getClientToken(token, {
          filename,
        });
        if (tokenResult.error) {
          setErrorMessage(tokenResult.error);
          setIsLoading(false);
          return;
        }

        const clientToken = tokenResult?.data?.token || false;
        if (!clientToken) {
          setErrorMessage("Failed to upload image. Please try again.");
          setIsLoading(false);
          return;
        }

        const blob: PutBlobResult = await put(filename, file, {
          access: "public",
          token: clientToken,
        });
        if (!blob) {
          setErrorMessage("Failed to upload image. Please try again.");
          setIsLoading(false);
          return;
        }

        images.push(blob?.url);
      }

      setIsLoading(false);
      setAdInProgress({
        ...values,
        images,
        location: currentLocation,
      });
      navigate("/dashboard/preview");
    },
    [
      values,
      selectedFiles,
      currentLocation,
      navigate,
      setErrorMessage,
      setIsLoading,
      setAdInProgress,
      token,
    ]
  );

  return (
    <>
      <GetVerifiedFirst
        show={showGetVerifiedFirst}
        reason={gateReason}
        onHide={() => {
          setShowGetVerifiedFirst(false);
          // Bounce back to the listing index — sitting on a half-rendered
          // create form they can't use is worse than landing on a known
          // safe page.
          navigate("/dashboard/listing");
        }}
        onSuccess={() => {
          setShowGetVerifiedFirst(false);
          navigate("/dashboard/profile");
        }}
        onCompleteProfile={() => {
          setShowGetVerifiedFirst(false);
          navigate("/dashboard/profile");
        }}
      />
      <CommonModal
        title="Link your Trustap account"
        message="You need to link your Trustap account before creating a listing. Without it, you won't be able to receive payouts when your artwork sells."
        show={showLinkTrustap}
        onHide={() => {
          setShowLinkTrustap(false);
          navigate("/dashboard/trustap-account");
        }}
      />
      <div className="bg-pink p-4 createListingMain">
        <div className="container-fluid px-0">
          <div className="row">
            <div className="col-12 col-xl-9 mx-auto">
              {/* Page header */}
              <div className="tableCardHeader px-0 mb-4">
                <div className="tableCardHeader__titleBlock">
                  <h2 className="tableCardHeader__title m-0">
                    Create new listing
                  </h2>
                  <p className="tableCardHeader__subtitle m-0">
                    Tell collectors about your artwork — photos, story, and
                    auction window.
                  </p>
                </div>
              </div>

              {adInProgress && (
                <div className="formStickyBar mb-3" style={{ position: "static" }}>
                  <span className="formStickyBar__msg">
                    You already have an ad in progress. Continue from that one?
                  </span>
                  <div className="formStickyBar__actions">
                    <Link to="/dashboard/preview" className="placeBidModal__submit" style={{ textDecoration: "none" }}>
                      Yes, continue
                    </Link>
                    <button
                      type="button"
                      className="placeBidModal__cancel"
                      onClick={() => {
                        setAdInProgress(null);
                        setSuccessMessage("Existing listing data is deleted.");
                      }}
                    >
                      Discard
                    </button>
                  </div>
                </div>
              )}

              {/* Section: Photos */}
              <div className="formSection">
                <div className="formSection__header">
                  <div className="formSection__iconWrap">
                    <i className="bi bi-images" aria-hidden="true" />
                  </div>
                  <div className="formSection__titleBlock">
                    <span className="formSection__kicker">Step 1</span>
                    <h3 className="formSection__title">Photos</h3>
                    <p className="formSection__subtitle">
                      Upload up to {Constants.MAX_FILES} images. The first one
                      becomes the cover.
                    </p>
                  </div>
                </div>

                <div
                  className={`uploadZone ${dragging ? "uploadZone--dragging" : ""}`}
                  onDragEnter={handleDragIn}
                  onDragOver={handleDragIn}
                  onDragLeave={handleDragOut}
                  onDrop={handleDrop}
                  onClick={() => inputFile.current?.click()}
                  role="button"
                  tabIndex={0}
                >
                  <span className="uploadZone__icon">
                    <i className="bi bi-cloud-arrow-up" aria-hidden="true" />
                  </span>
                  <h4 className="uploadZone__title">
                    Drag &amp; drop images here
                  </h4>
                  <p className="uploadZone__hint">
                    PNG or JPG · {selectedFiles.length} of {Constants.MAX_FILES}{" "}
                    selected
                  </p>
                  <button
                    type="button"
                    className="uploadZone__browseBtn"
                    onClick={(e) => {
                      e.stopPropagation();
                      inputFile.current?.click();
                    }}
                  >
                    <i className="bi bi-folder2-open" aria-hidden="true" />
                    <span>Browse files</span>
                  </button>
                  <input
                    id="txt-listing-pictures"
                    type="file"
                    accept="image/png, image/jpg, image/jpeg"
                    style={{ display: "none" }}
                    ref={inputFile}
                    multiple
                    onChange={handleFileChange}
                  />
                </div>

                {selectedFiles.length > 0 && (
                  <div className="uploadGrid">
                    {selectedFiles.map((file, index) => (
                      <div
                        key={index}
                        className="uploadGrid__item"
                        role="button"
                        tabIndex={0}
                        aria-label={`Preview ${file?.name}`}
                        onClick={() => setLightboxSrc(file?.img)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            setLightboxSrc(file?.img);
                          }
                        }}
                      >
                        <img
                          src={file?.img}
                          alt={`File ${index + 1}`}
                          className="uploadGrid__img"
                        />
                        <button
                          type="button"
                          className="uploadGrid__remove"
                          aria-label={`Remove ${file?.name}`}
                          onClick={(e) => handleRemoveImage(e, file?.name)}
                        >
                          <i className="bi bi-x-lg" aria-hidden="true" />
                        </button>
                        <span className="uploadGrid__name" title={file?.name}>
                          {file?.name}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Section: Basics */}
              <div className="formSection">
                <div className="formSection__header">
                  <div className="formSection__iconWrap">
                    <i className="bi bi-pencil-square" aria-hidden="true" />
                  </div>
                  <div className="formSection__titleBlock">
                    <span className="formSection__kicker">Step 2</span>
                    <h3 className="formSection__title">Basics</h3>
                    <p className="formSection__subtitle">
                      A title, the right category, and a short description.
                    </p>
                  </div>
                </div>

                <Text
                  name="title"
                  label="Title"
                  placeholder="A sky beneath the stars"
                  value={values?.title}
                  onChange={handleInputChange}
                  required
                />

                <div className="row">
                  <div className="col-md-6">
                    <div className="field mb-3">
                      <label className="field__label">
                        Category<span className="field__required">*</span>
                      </label>
                      <CategoryDropdown
                        name="categoryId"
                        value={values?.categoryId}
                        onChange={handleInputChange}
                      />
                      {values?.categoryId === others?.category?._id && (
                        <Text
                          name="otherCategoryName"
                          placeholder="Enter category name"
                          value={values?.otherCategoryName}
                          onChange={handleInputChange}
                          parentStyles={{ marginTop: 8 }}
                        />
                      )}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="field mb-3">
                      <label className="field__label">
                        Sub Category<span className="field__required">*</span>
                      </label>
                      <SubCategoryDropdown
                        categoryId={values?.categoryId}
                        name="subCategoryId"
                        value={values?.subCategoryId}
                        onChange={handleInputChange}
                        onClick={(e: any) => {
                          e?.preventDefault();
                          if (!values?.categoryId) {
                            setInfoMessage(
                              "Please choose a category first."
                            );
                          }
                        }}
                      />
                      {values?.subCategoryId === others?.subCategory?._id && (
                        <Text
                          name="otherSubCategoryName"
                          placeholder="Enter sub category name"
                          value={values?.otherSubCategoryName}
                          onChange={handleInputChange}
                          parentStyles={{ marginTop: 8 }}
                        />
                      )}
                    </div>
                  </div>
                </div>

                <div className="field mb-3">
                  <label className="field__label">
                    Medium<span className="field__required">*</span>
                  </label>
                  <MediumDropdown
                    categoryId={values?.categoryId}
                    name="mediumId"
                    value={values?.mediumId}
                    onChange={handleInputChange}
                    onClick={(e: any) => {
                      e?.preventDefault();
                      if (!values?.categoryId) {
                        setInfoMessage("Please choose a category first.");
                      }
                    }}
                  />
                  {values?.mediumId === others?.medium?._id && (
                    <Text
                      name="otherMediumName"
                      placeholder="Enter medium name"
                      value={values?.otherMediumName}
                      onChange={handleInputChange}
                      parentStyles={{ marginTop: 8 }}
                    />
                  )}
                </div>

                <Form.Group className="field mb-0">
                  <label className="field__label">
                    Description<span className="field__required">*</span>
                  </label>
                  <Form.Control
                    name="description"
                    as="textarea"
                    rows={5}
                    placeholder="Tell collectors about the inspiration, technique, story behind this piece…"
                    value={values?.description}
                    onChange={handleInputChange}
                  />
                  <div className="field__meta">
                    <span className="field__hint">
                      {values?.description
                        ? `${values.description.length} characters`
                        : "Aim for 80–300 characters."}
                    </span>
                  </div>
                </Form.Group>
              </div>

              {/* Section: Specifications */}
              <div className="formSection">
                <div className="formSection__header">
                  <div className="formSection__iconWrap">
                    <i className="bi bi-rulers" aria-hidden="true" />
                  </div>
                  <div className="formSection__titleBlock">
                    <span className="formSection__kicker">Step 3</span>
                    <h3 className="formSection__title">Specifications</h3>
                    <p className="formSection__subtitle">
                      Used for shipping quotes and the buyer's specs panel.
                    </p>
                  </div>
                </div>

                <label className="field__label">
                  Dimensions<span className="field__required">*</span>
                </label>
                <div className="dimensionsRow mb-3">
                  <Number
                    name="height"
                    value={values?.height}
                    onChange={handleInputChange}
                    suffix=" in"
                    thousandSeparator={true}
                    placeholder="Height"
                  />
                  <span className="dimensionsRow__sep">×</span>
                  <Number
                    name="width"
                    value={values?.width}
                    onChange={handleInputChange}
                    suffix=" in"
                    thousandSeparator={true}
                    placeholder="Width"
                  />
                  <span className="dimensionsRow__sep">×</span>
                  <Number
                    name="depth"
                    value={values?.depth}
                    onChange={handleInputChange}
                    suffix=" in"
                    thousandSeparator={true}
                    placeholder="Depth"
                  />
                </div>

                <Number
                  name="weight"
                  label="Weight"
                  placeholder="0"
                  value={values?.weight}
                  onChange={handleInputChange}
                  suffix=" lbs"
                  thousandSeparator={true}
                  required
                />
              </div>

              {/* Section: Pricing & schedule */}
              <div className="formSection">
                <div className="formSection__header">
                  <div className="formSection__iconWrap">
                    <i className="bi bi-cash-coin" aria-hidden="true" />
                  </div>
                  <div className="formSection__titleBlock">
                    <span className="formSection__kicker">Step 4</span>
                    <h3 className="formSection__title">
                      Pricing &amp; schedule
                    </h3>
                    <p className="formSection__subtitle">
                      Set the opening bid and the auction window.
                    </p>
                  </div>
                </div>

                <Number
                  name="price"
                  label="Opening bid price"
                  placeholder="0"
                  value={values?.price}
                  onChange={handleInputChange}
                  prefix="$"
                  thousandSeparator={true}
                  required
                  hint={`Minimum amount: ${Utils.formatToCurrency(
                    Constants.ARTWORK_MIN_PRICE.toString()
                  )}`}
                />

                <Timezone
                  name="timezone"
                  label="Timezone"
                  value={values?.timezone}
                  onChange={handleInputChange}
                />

                <div className="row">
                  <div className="col-md-6">
                    <Date
                      name="biddingStarts"
                      label="Bid opens on"
                      placeholder="Pick a date & time"
                      value={values?.biddingStarts}
                      onChange={handleInputChange}
                      showTimeInput={true}
                      hint={`Earliest opening is ${Constants.BIDDING_STARTS_MIN_DAYS_AHEAD} days from today.`}
                      minDate={moment()
                        .add(Constants.BIDDING_STARTS_MIN_DAYS_AHEAD, "days")
                        .startOf("day")
                        .toDate()}
                      maxDate={moment().add(3, "months").toDate()}
                      filterTime={(t: Date) =>
                        moment(t).isSameOrAfter(
                          moment()
                            .add(Constants.BIDDING_STARTS_MIN_DAYS_AHEAD, "days")
                            .startOf("day")
                        )
                      }
                      required
                    />
                  </div>
                  <div className="col-md-6">
                    <Date
                      name="biddingEnds"
                      label="Bid ends on"
                      placeholder="Pick a date & time"
                      value={values?.biddingEnds}
                      onChange={handleInputChange}
                      showTimeInput={true}
                      hint={Utils.formatBidEndsHint(
                        values?.biddingEnds,
                        values?.timezone
                      )}
                      minDate={
                        values?.biddingStarts
                          ? moment(values.biddingStarts).toDate()
                          : moment().add(1, "days").toDate()
                      }
                      maxDate={
                        values?.biddingStarts
                          ? moment(values.biddingStarts)
                              .add(3, "months")
                              .toDate()
                          : moment().add(3, "months").toDate()
                      }
                      filterTime={(t: Date) =>
                        values?.biddingStarts
                          ? moment(t).isAfter(moment(values.biddingStarts))
                          : true
                      }
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Sticky action bar */}
              <div className="formStickyBar">
                {validationMessage ? (
                  <span className="formStickyBar__msg formStickyBar__msg--error">
                    <i
                      className="bi bi-exclamation-circle"
                      aria-hidden="true"
                    />
                    {validationMessage}
                  </span>
                ) : (
                  <span className="formStickyBar__msg">
                    Review on the next screen before publishing.
                  </span>
                )}
                <div className="formStickyBar__actions">
                  <button
                    type="button"
                    className="placeBidModal__cancel"
                    onClick={() => navigate("/dashboard/listing")}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="placeBidModal__submit"
                    onClick={(e: any) => {
                      const gate = Utils.canCreateListing(user, isVerifiedUser);
                      if (gate.ok) {
                        handleSubmit(e);
                      } else {
                        setGateReason(gate.reason);
                        setShowGetVerifiedFirst(true);
                      }
                    }}
                  >
                    <i className="bi bi-arrow-right" aria-hidden="true" />
                    <span>Continue to preview</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <ImageLightbox
        src={lightboxSrc}
        onClose={() => setLightboxSrc(null)}
      />
    </>
  );
}

export default Create;
